/**
 * Consultas e gravações das manutenções pelo TanStack Query.
 *
 * Papel: aqui ficam `useCorretivas` e `usePreventivas`, que mantêm em cache o
 * que a tela lê, e as quatro mutações que gravam e mandam a listagem
 * correspondente se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_MANUTENCOES`, e cada
 * mutação invalida apenas o ramo que alterou — os dois cadastros não se
 * referenciam, e invalidar os dois faria a tela buscar cento e poucas linhas
 * para mostrar uma que mudou.
 *
 * `usePreventivas` recebe interruptor porque a tela inicial a chama para montar
 * o aviso de vencimento, e quem não tem o módulo liberado receberia 403 a cada
 * abertura daquela tela.
 *
 * As duas exclusões tratam o 404 como sucesso: o registro já não existe, que é
 * o que a pessoa queria, e a lista precisa ser relida do mesmo jeito. Sem isso,
 * excluir o que outra pessoa excluiu antes mostraria "Não foi possível excluir."
 * com a caixa aberta, e nenhum clique a mais resolveria.
 */
import { type NovaCorretiva, type NovaPreventiva } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '@/shared/api';

import {
  atualizarCorretiva,
  atualizarPreventiva,
  buscarCorretivas,
  buscarPreventivas,
  criarCorretiva,
  criarPreventiva,
  excluirCorretiva,
  excluirPreventiva,
} from '../api/manutencoes';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_MANUTENCOES = ['manutencoes'] as const;

/** Chave da lista de ocorrências corretivas. */
export const CHAVE_CORRETIVAS = [...CHAVE_MANUTENCOES, 'corretivas'] as const;

/** Chave da lista de rotinas preventivas. */
export const CHAVE_PREVENTIVAS = [...CHAVE_MANUTENCOES, 'preventivas'] as const;

/**
 * Lê as ocorrências corretivas.
 *
 * @param habilitado - `false` desliga a consulta, para quem não tem o módulo.
 * @returns Resultado da consulta, na ordem de `posicao`.
 */
export function useCorretivas(habilitado = true) {
  return useQuery({
    queryKey: CHAVE_CORRETIVAS,
    queryFn: ({ signal }) => buscarCorretivas(signal),
    enabled: habilitado,
  });
}

/**
 * Lê as rotinas preventivas.
 *
 * @param habilitado - `false` desliga a consulta, para quem não tem o módulo.
 * @returns Resultado da consulta, na ordem de `posicao`.
 */
export function usePreventivas(habilitado = true) {
  return useQuery({
    queryKey: CHAVE_PREVENTIVAS,
    queryFn: ({ signal }) => buscarPreventivas(signal),
    enabled: habilitado,
  });
}

/** Variáveis das mutações que criam ou alteram conforme venha identificador. */
interface SalvarVariaveis<T> {
  /** Identificador do registro a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: T;
}

/**
 * Apaga um registro, tratando como apagado o que a API já não encontra.
 *
 * @param excluir - A chamada de exclusão de `api/manutencoes.ts`.
 * @param id - O identificador do registro.
 * @returns Promessa cumprida quando o registro não existe mais.
 */
async function excluirMesmoAusente(
  excluir: (id: string) => Promise<void>,
  id: string,
): Promise<void> {
  try {
    await excluir(id);
  } catch (erro) {
    if (erro instanceof ErroDaApi && erro.status === 404) return;
    throw erro;
  }
}

/**
 * Cria ou altera uma ocorrência corretiva, conforme venha identificador.
 *
 * @returns A mutação, que recebe `id` e os dados.
 */
export function useSalvarCorretiva() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarVariaveis<NovaCorretiva>) =>
      id === null ? criarCorretiva(dados) : atualizarCorretiva(id, dados),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_CORRETIVAS }),
  });
}

/**
 * Apaga uma ocorrência corretiva.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirCorretiva() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirMesmoAusente(excluirCorretiva, id),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_CORRETIVAS }),
  });
}

/**
 * Cria ou altera uma rotina preventiva, conforme venha identificador.
 *
 * @returns A mutação, que recebe `id` e os dados.
 */
export function useSalvarPreventiva() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarVariaveis<NovaPreventiva>) =>
      id === null ? criarPreventiva(dados) : atualizarPreventiva(id, dados),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_PREVENTIVAS }),
  });
}

/**
 * Apaga uma rotina preventiva.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirPreventiva() {
  const clienteDeConsultas = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => excluirMesmoAusente(excluirPreventiva, id),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_PREVENTIVAS }),
  });
}
